<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content"
    @before-close="closeModal"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
              <path d="M4 22h16"></path>
              <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
              <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
            </svg>
          </div>
          <h5 class="modal-title">{{ isEdit ? 'Edit' : 'New' }} Training Program</h5>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeModal" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body">

      <div class="setting-item">
        <label for="programTitle">Program Title <span class="text-danger">*</span></label>
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </div>
          <input
            id="programTitle"
            v-model="form.title"
            type="text"
            class="form-control"
            :class="{ 'border-danger': v$.title.$error }"
            placeholder="e.g. Annual Security Training"
            autofocus
            @blur="v$.title.$touch()"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.title.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">Program title is required.</div>
        </div>
      </div>

      <div class="settings-grid">
        <div class="setting-item mb-2">
          <label for="frequencySelect">Assignment Frequency</label>
          <div class="input-with-icon">
            <div class="input-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <select id="frequencySelect" class="form-control" v-model="form.assignment_frequency">
              <option value="On Demand">On Demand</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Annually">Annually</option>
            </select>
          </div>

         <div class="explainer-box" v-html="dynamicExplainer"></div>

        </div>

        <div class="setting-item mt-4">
        <div class="d-flex justify-content-between align-items-center bg-light-surface p-3 rounded border mb-2">
          <div>
            <label class="mb-0 text-dark fw-bold"><i class="fas fa-bell text-theme me-2"></i> Training Reminders</label>
            <small class="d-block text-muted mt-1" style="font-size: 0.75rem;">
              Send automated email reminders to users with incomplete trainings.
            </small>
          </div>
          <label class="pure-toggle mb-0" data-testid="toggle-program-reminder">
            <input type="checkbox" v-model="form.enable_reminders">
            <span class="pure-slider"></span>
          </label>
        </div>

        <div class="d-flex justify-content-between align-items-center bg-light-surface p-3 rounded border">
          <div>
            <label class="mb-0 text-dark fw-bold"><i class="fas fa-clipboard-check text-theme me-2"></i> Mandatory Training?</label>
            <small class="d-block text-muted mt-1" style="font-size: 0.75rem;">
              If disabled, this program will not count towards dashboard compliance metrics and overdue reports.
            </small>
          </div>
          <label class="pure-toggle mb-0" data-testid="toggle-program-mandatory">
            <input type="checkbox" v-model="form.is_mandatory">
            <span class="pure-slider"></span>
          </label>
        </div>
      </div>
      </div>

      <div
        class="status-card"
        :class="{
          'is-active': form.status === 'published',
          'is-locked': form.status === 'locked'
        }"
      >
        <div class="status-info">
          <div class="status-header">
            <span class="status-title">Program Status</span>
            <span v-if="form.status === 'published'" class="status-badge active">Published</span>
            <span v-else-if="form.status === 'locked'" class="status-badge locked">Locked</span>
            <span v-else class="status-badge inactive">Draft</span>
          </div>
          <p class="status-description">
            <template v-if="form.status === 'published'">
              This program is active and visible for assignment.
            </template>
            <template v-else-if="form.status === 'locked'">
              This program is locked. No new users can be assigned.
            </template>
            <template v-else>
              This program is currently hidden and cannot be assigned yet.
            </template>
          </p>
        </div>

        <div class="status-control">
          <select class="form-control status-dropdown" v-model="form.status">
            <option value="draft" :disabled="isEdit && initialData.status !== 'draft'">
              Draft
            </option>
            <option value="published">Published</option>
            <option value="locked">Locked</option>
          </select>
        </div>
      </div>

    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
      <button type="button" class="btn btn-primary" @click="handleSave">Save Program</button>
    </div>
  </vue-final-modal>
</template>

<script setup>
import { reactive, computed, watch, defineEmits, defineProps } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

const props = defineProps(['modelValue', 'isEdit', 'initialData', 'categoryId']);
const emit = defineEmits(['update:modelValue', 'save']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const form = reactive({
  title: '',
  assignment_frequency: 'On Demand',
  status: 'draft',
  category_id: null,
  enable_reminders: true,
  is_mandatory: true
});

const rules = computed(() => ({
  title: { required }
}));

const v$ = useVuelidate(rules, form);

const dynamicExplainer = computed(() => {
  const freq = form.assignment_frequency;
  const count = props.initialData?.modules?.length || 0;

  if (freq === 'On Demand') {
    return "This program has no automated schedule. Users will only receive modules when <strong>manually assigned</strong> or through ad-hoc requests.";
  }

  let totalDays = 0;
  let exampleText = "";

  if (freq === 'Monthly') {
    totalDays = 30;
    exampleText = "For example, 4 training modules in a monthly program would mean one module is assigned approx. every <strong>7 days</strong>.";
  } else if (freq === 'Quarterly') {
    totalDays = 90;
    exampleText = "For example, 6 training modules in a quarterly program would mean one module is assigned approx. every <strong>15 days</strong>.";
  } else if (freq === 'Annually') {
    totalDays = 365;
    exampleText = "For example, 24 training modules in an annual program would mean one module is assigned approx. every <strong>2 weeks</strong>.";
  }

  // If it's a brand new program or has 0 modules, show the helpful example
  if (count === 0) {
    return `Training video/module assignment frequency is determined by how many training items are in the Program. ${exampleText}`;
  }

  // If there are modules, calculate the exact real-world interval!
  const intervalDays = Math.max(1, Math.round(totalDays / count));
  const pluralItem = count > 1 ? 'items' : 'item';
  const pluralDay = intervalDays > 1 ? 'days' : 'day';

  return `This program currently contains <strong>${count} training ${pluralItem}</strong>. Based on a ${freq} schedule (${totalDays} days), the system will automatically assign one module to users approximately every <strong>${intervalDays} ${pluralDay}</strong>.`;
});

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    form.title = props.initialData?.title || '';
    form.assignment_frequency = props.initialData?.assignment_frequency || 'On Demand';
    form.status = props.initialData?.status || 'draft';
    form.category_id = props.categoryId;

    form.enable_reminders = props.initialData?.enable_reminders !== false;
    form.is_mandatory = props.initialData?.is_mandatory !== false;

    v$.value.$reset();
  }
});

const closeModal = () => {
  isOpen.value = false;
};

const handleSave = async () => {
  const isFormValid = await v$.value.$validate();

  if (!isFormValid) return;

  emit('save', { ...form });
};
</script>

<style scoped>
/* Explainer Callout Box */
.explainer-box {
  margin-top: 1rem;
  padding: 0.85rem 1.25rem;
  background-color: #f8fafc;
  border-left: 4px solid #00a0c1; /* Brand color accent */
  border-radius: 0 6px 6px 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}

.explainer-box strong {
  color: #334155;
  font-weight: 600;
}

/* Modal Overlay & Container */
:deep(.modal-overlay-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

:deep(.modern-modal-content) {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 520px; /* Slightly wider to accommodate the grid and card nicely */
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  border: none !important;
  padding: 0;
  margin: 0 1rem;
  overflow: hidden;
}

/* Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  color: #00a0c1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 160, 193, 0.1);
  padding: 8px;
  border-radius: 8px;
}

.modal-title {
  color: #1e293b;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.close-button {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
}

.close-button:hover {
  background-color: #fee2e2;
  color: #dc3545;
  transform: rotate(90deg);
}

/* Body */
.modal-body {
  padding: 1.25rem 1.5rem;
  flex-grow: 1;
}

.setting-item {
  margin-bottom: 1.25rem;
}

.setting-item label {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: #64748b;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

.form-control {
  width: 100%;
  padding: 0.7rem 0.7rem 0.7rem 2.5rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;
  appearance: auto; /* Ensure select dropdown arrow shows */
}

.form-control:focus {
  border-color: #00a0c1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.settings-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
}

/* Status Card styling exactly like your reference */
.status-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
}

.status-card.is-active {
  background-color: #f0f9ff;
  border-color: #bae6fd;
}

.status-info {
  flex: 1;
  padding-right: 1rem;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.status-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #1e293b;
}

.status-description {
  margin: 0;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
}

.status-badge {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.025em;
}

.status-badge.active {
  background-color: #dcfce7;
  color: #15803d;
}

.status-badge.inactive {
  background-color: #f1f5f9;
  color: #64748b;
}

/* Custom Switch for Status */
.custom-switch {
  position: relative;
  display: flex;
  align-items: center;
}

.custom-switch .form-check-input {
  width: 2.8rem;
  height: 1.4rem;
  margin-top: 0;
  cursor: pointer;
  background-color: #cbd5e1;
  border-color: transparent;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
}

.custom-switch .form-check-input:checked {
  background-color: #00a0c1;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.5rem;
  gap: 0.75rem;
  background-color: #f8fafc;
}

.btn {
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.btn-primary {
  background-color: #00a0c1;
  color: white;
  border-color: #00a0c1;
}

.btn-primary:hover {
  background-color: #008da8;
  border-color: #008da8;
}

.btn-secondary {
  background-color: white;
  color: #64748b;
  border-color: #e2e8f0;
}

.btn-secondary:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.status-badge.locked {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-card.is-locked {
  background-color: #fef2f2;
  border-color: #fecaca;
}

.status-dropdown {
  width: auto;
  min-width: 120px;
  padding: 0.4rem 2rem 0.4rem 1rem; /* Adjust padding for the smaller size */
  font-weight: 600;
  font-size: 0.85rem;
  color: #475569;
  cursor: pointer;
}

/* Custom Pure CSS Toggle Switch */
.pure-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}
.pure-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}
.pure-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: .3s;
  border-radius: 24px;
}
.pure-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #94a3b8;
  transition: .3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
.pure-toggle input:checked + .pure-slider {
  background-color: #B5DAC6;
}
.pure-toggle input:checked + .pure-slider:before {
  transform: translateX(20px);
  background-color: #00B972;
}
</style>